import type { DatadogLogEntry, DatadogLogStatus, LogLevel } from "./types";

import { datadogEnv } from "./datadog-env";
import { emitBrowserLogs } from "./emit-browser-logs";
import { formatStructuredConsoleFallback } from "./format-structured-console-fallback";
import { getTagsFromPayload } from "./get-tags-from-payload";
import {
  isStructuredLogInput,
  normalizeStructuredLog,
} from "./normalize-structured-log";
import { scheduleServerLogSubmission } from "./schedule-server-log-submission";
import { submitLogs } from "./submit-logs";

/**
 * Format all messages. JSON.stringify any objects, pass through primitives
 */
function formatMessages(messages: unknown[]) {
  return messages.map((message: unknown) => {
    if (message instanceof Error) {
      return message;
    }

    if (message !== null && typeof message === "object") {
      try {
        return JSON.stringify(message);
      } catch {
        return message;
      }
    }

    return message;
  });
}

function isBrowserRuntime(): boolean {
  return typeof window !== "undefined";
}

function toDatadogEntries(
  payloads: ReturnType<typeof normalizeStructuredLog>[],
): DatadogLogEntry[] {
  const { environment, service } = datadogEnv();

  return payloads.map((payload) => ({
    ddtags: getTagsFromPayload(payload),
    environment,
    message: payload.message,
    service: payload.service ?? service,
    status: payload.level || "info",
    ...(payload.data ? { extra_data: payload.data } : {}),
  }));
}

function logServerConsoleFallback(
  payloads: ReturnType<typeof normalizeStructuredLog>[],
): void {
  console.warn(
    "Datadog logging disabled. Logging to console instead:",
    formatStructuredConsoleFallback(payloads),
  );
}

function deliverServerLogs(entries: DatadogLogEntry[]): void {
  void scheduleServerLogSubmission(() =>
    submitLogs(entries).catch((error: unknown) => {
      console.error(
        "Failed to send logs via Datadog Util:",
        error instanceof Error ? error.message : error,
      );
    }),
  );
}

function emitStructuredLogs(
  payloads: ReturnType<typeof normalizeStructuredLog>[],
): void {
  if (payloads.length === 0) {
    return;
  }

  if (isBrowserRuntime()) {
    void emitBrowserLogs(payloads);
    return;
  }

  const { apiKey } = datadogEnv();
  if (!apiKey) {
    logServerConsoleFallback(payloads);
    return;
  }

  deliverServerLogs(toDatadogEntries(payloads));
}

function dispatchToLogger(
  defaultLevel: DatadogLogStatus,
  consoleLevel: LogLevel,
  messages: unknown[],
): void {
  const [first, ...rest] = messages;

  if (rest.length === 0 && isStructuredLogInput(first)) {
    emitStructuredLogs([normalizeStructuredLog(first, defaultLevel)]);
    return;
  }

  if (rest.length === 0 && Array.isArray(first)) {
    if (first.length === 0) {
      return;
    }

    if (first.every(isStructuredLogInput)) {
      emitStructuredLogs(
        first.map((payload) => normalizeStructuredLog(payload, defaultLevel)),
      );
      return;
    }
  }

  console[consoleLevel](...formatMessages(messages));
}

function log(...messages: unknown[]): void {
  dispatchToLogger("info", "log", messages);
}

function logDebug(...messages: unknown[]): void {
  dispatchToLogger("debug", "debug", messages);
}

function logError(...messages: unknown[]): void {
  dispatchToLogger("error", "error", messages);
}

function logInfo(...messages: unknown[]): void {
  dispatchToLogger("info", "info", messages);
}

function logWarn(...messages: unknown[]): void {
  dispatchToLogger("warn", "warn", messages);
}

export { log, logDebug, logError, logInfo, logWarn };
